/**
 * 「サイトから提案を受ける」(brand_site_proposal) の実行状態 (B 群と CreateBrandModal 改で共用)。
 * 投入 → 進捗 (縦ステッパーの段階) → 結果 / エラーまでを持つ。反映 (フォームへの適用) は呼び出し側。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCreditBadgeText } from '../../components/generation/hooks/useGenerationCredits';
import { useCreditRates } from '../organizations/hooks';
import {
  createBrandSiteProposalJob,
  formatSiteProposalElapsed,
  siteProposalCreditsPerRun,
  siteProposalJobErrorMessage,
  siteProposalRequestErrorMessage,
  waitForBrandSiteProposal,
  type SiteProposalProgress,
  type SiteProposalResult,
} from './siteProposal';

export type SiteProposalPhase = 'idle' | 'running' | 'done' | 'error';

export interface BrandSiteProposalState {
  phase: SiteProposalPhase;
  progress: SiteProposalProgress | null;
  result: SiteProposalResult | null;
  error: string | null;
  /** 「1分05秒」形式の経過時間 (実行中だけ) */
  elapsed: string;
  /** 1 回のポイント数 (料金未設定は null = 受け付けられない) */
  creditsPerRun: number | null;
  /** ボタンに出すポイント表記 */
  creditBadge: string | number;
  /** 投入して完了を待つ。成功なら結果、失敗 (受付・実行) なら null (error に文言) */
  start: (url: string, brandId?: string | null) => Promise<SiteProposalResult | null>;
  /** 画面側の待機をやめる (サーバー側のジョブは続く) */
  abort: () => void;
  reset: () => void;
}

export function useBrandSiteProposal(): BrandSiteProposalState {
  const queryClient = useQueryClient();
  const creditRates = useCreditRates();
  const creditsPerRun = siteProposalCreditsPerRun(creditRates.data?.rates);
  const creditBadge = useCreditBadgeText(creditsPerRun);
  const [phase, setPhase] = useState<SiteProposalPhase>('idle');
  const [progress, setProgress] = useState<SiteProposalProgress | null>(null);
  const [result, setResult] = useState<SiteProposalResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  // 離脱時の中断は 1 ティック遅らせる (開発モードの StrictMode の疑似アンマウントで実行中の待機を捨てないため)
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.setTimeout(() => {
        if (!mountedRef.current) abortRef.current?.abort();
      }, 0);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'running' || startedAt == null) {
      setElapsed('');
      return;
    }
    const tick = () => setElapsed(formatSiteProposalElapsed(Date.now() - startedAt));
    tick();
    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [phase, startedAt]);

  const refreshCredits = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['organization', 'credits'] });
    void queryClient.invalidateQueries({ queryKey: ['credits'] });
  }, [queryClient]);

  const start = useCallback(async (url: string, brandId?: string | null): Promise<SiteProposalResult | null> => {
    if (creditsPerRun === null) {
      setPhase('error');
      setError('サイトからの提案の料金が未設定のため、現在ご利用いただけません');
      return null;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('running');
    setProgress(null);
    setError(null);
    setResult(null);
    setStartedAt(Date.now());
    let jobId: string;
    try {
      const res = await createBrandSiteProposalJob({ url, quoted_credits: creditsPerRun, brand_id: brandId ?? null });
      jobId = res.job_id;
      refreshCredits();
    } catch (err) {
      if (controller.signal.aborted) return null;
      setPhase('error');
      setError(siteProposalRequestErrorMessage(err));
      return null;
    }
    try {
      const data = await waitForBrandSiteProposal(jobId, {
        signal: controller.signal,
        onProgress: (job) => {
          if (controller.signal.aborted) return;
          setProgress((job.result_data ?? null) as SiteProposalProgress | null);
        },
      });
      if (controller.signal.aborted) return null;
      setResult(data);
      setPhase('done');
      return data;
    } catch (err) {
      if (controller.signal.aborted) return null;
      setPhase('error');
      setError(siteProposalJobErrorMessage(err));
      // 失敗したジョブはポイントが返る
      refreshCredits();
      return null;
    }
  }, [creditsPerRun, refreshCredits]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setPhase('idle');
    setProgress(null);
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setPhase('idle');
    setProgress(null);
    setResult(null);
    setError(null);
    setStartedAt(null);
  }, []);

  return { phase, progress, result, error, elapsed, creditsPerRun, creditBadge, start, abort, reset };
}
